import type { CreateGhostProfileData } from '@hooks'
// Imported from the source module (not the '@utils' barrel) so this pure parser
// stays unit-testable without pulling in the toast/UI config the barrel loads.
import { Scope } from '../../../../utils/constants'

// Reads a CRM export (CSV only) into a header row + data rows, auto-detects
// which column maps to which contact field (French + English synonyms), and
// turns each row into a ready-to-create ghost-profile payload.
//
// CSV-only by design: the previous Excel path used SheetJS (`xlsx`), affected by
// CVE-2023-30533 (prototype pollution during XLSX.read) and CVE-2024-22363
// (ReDoS) with no patched build on the npm registry. Parsing untrusted uploads
// with it was an unacceptable attack surface, so the binary parser was dropped
// in favour of the hand-rolled, safe CSV reader below.

// Thrown when a non-CSV file (e.g. .xlsx/.xls) reaches the parser. The UI catches
// this sentinel and shows a translated "unsupported format" message.
export const UNSUPPORTED_FORMAT_ERROR = 'UNSUPPORTED_FORMAT'

export type CanonicalField =
  | 'firstName'
  | 'lastName'
  | 'fullName'
  | 'email'
  | 'phone'
  | 'company'
  | 'jobTitle'
  | 'website'
  | 'linkedin'
  | 'instagram'
  | 'facebook'
  | 'twitter'
  | 'notes'

export interface ParsedFile {
  headers: string[]
  rows: string[][]
  // headerIndex -> canonical field (or null when no confident match)
  mapping: (CanonicalField | null)[]
}

// Accent-insensitive, punctuation-insensitive normalisation for header matching.
const normalize = (s: string): string =>
  (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

// Ordered list of synonyms per field. First confident match wins.
const SYNONYMS: Record<CanonicalField, string[]> = {
  firstName: ['prenom', 'firstname', 'givenname', 'first'],
  lastName: ['nomdefamille', 'lastname', 'surname', 'familyname', 'nom'],
  fullName: ['nomcomplet', 'fullname', 'name', 'contact', 'nomprenom', 'prenomnom'],
  email: ['email', 'mail', 'courriel', 'adressemail', 'emailaddress', 'mailaddress'],
  phone: [
    'telephone', 'phone', 'mobile', 'gsm', 'tel', 'numero', 'portable',
    'phonenumber', 'numerodetelephone', 'tellphone', 'cell', 'cellphone',
  ],
  company: [
    'entreprise', 'societe', 'company', 'organization', 'organisation',
    'employer', 'employeur', 'compagnie', 'companyname', 'raisonsociale',
  ],
  jobTitle: ['poste', 'fonction', 'profession', 'metier', 'jobtitle', 'title', 'job', 'role', 'titre'],
  website: ['site', 'siteweb', 'website', 'web', 'url', 'siteinternet'],
  linkedin: ['linkedin', 'linkedinurl', 'profillinkedin'],
  instagram: ['instagram', 'insta', 'instagramhandle'],
  facebook: ['facebook', 'fb', 'facebookurl'],
  twitter: ['twitter', 'x', 'twitterhandle'],
  notes: ['notes', 'note', 'description', 'bio', 'remarque', 'remarques', 'commentaire', 'commentaires'],
}

// Field priority so that, e.g. "nom" doesn't steal the slot from "nom complet"
// when both could match: more specific fields are resolved first.
const FIELD_ORDER: CanonicalField[] = [
  'firstName', 'lastName', 'fullName', 'email', 'phone', 'company',
  'jobTitle', 'website', 'linkedin', 'instagram', 'facebook', 'twitter', 'notes',
]

// Column names of the usual exports (Google Contacts in its two formats,
// Outlook) that no synonym reads safely: "E-mail 1 - Value" sits next to
// "E-mail 1 - Label", whose cells hold "* Work".
const KNOWN_HEADERS: Record<string, CanonicalField> = {
  email1value: 'email',
  phone1value: 'phone',
  mobilephone: 'phone',
  organizationname: 'company',
  organization1name: 'company',
  organizationtitle: 'jobTitle',
  organization1title: 'jobTitle',
  website1value: 'website',
  webpage: 'website',
}

// Words of a column name, accents and capitals removed ("E-mail pro" -> e, mail, pro).
const tokens = (s: string): string[] =>
  (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)

// A column whose name holds one of these words is never guessed: it describes
// another column ("E-mail 1 - Label") or is something else ("Phonetic First
// Name", "Numéro de TVA", "Fax").
const EXCLUDED = new Set([
  'phonetic', 'yomi', 'label', 'type', 'prefix', 'suffix', 'middle', 'nickname',
  'maiden', 'tva', 'vat', 'fax', 'siret', 'siren', 'id',
])

// Synonyms too short or too common to be trusted inside a longer column name.
// They still count when they are the whole name.
const WEAK = new Set([
  'x', 'name', 'first', 'nom', 'contact', 'title', 'titre', 'web', 'site', 'job',
  'role', 'numero', 'note', 'fb',
])

// True when consecutive words of the column name, put together, are the synonym
// ("E-mail pro" -> "email", "Téléphone portable" -> "telephone").
const hasWordRun = (words: string[], synonym: string): boolean => {
  for (let start = 0; start < words.length; start++) {
    let run = ''
    for (let end = start; end < words.length && run.length < synonym.length; end++) {
      run += words[end]
      if (run === synonym) return true
    }
  }
  return false
}

export const detectMapping = (headers: string[]): (CanonicalField | null)[] => {
  const normalized = headers.map(normalize)
  const mapping: (CanonicalField | null)[] = headers.map(() => null)
  const used = new Set<number>()
  const matched = new Set<CanonicalField>()

  const assign = (field: CanonicalField, idx: number) => {
    mapping[idx] = field
    used.add(idx)
    matched.add(field)
  }

  // Pass 0 — column names of the usual exports, known by heart.
  normalized.forEach((h, i) => {
    const field = KNOWN_HEADERS[h]
    if (field && !matched.has(field)) assign(field, i)
  })

  // Pass 1 — exact header matches for every field. Done first across ALL fields
  // so a specific header like "nom complet" maps to fullName before a looser
  // rule (lastName, "nom") could steal it.
  for (const field of FIELD_ORDER) {
    if (matched.has(field)) continue
    const idx = normalized.findIndex((h, i) => !used.has(i) && SYNONYMS[field].includes(h))
    if (idx !== -1) assign(field, idx)
  }

  // Pass 2 — whole-word matches for fields still unmatched, so "E-mail pro" or
  // "Téléphone portable" are still recognised. Whole words only: looking for the
  // synonym anywhere in the name took "Phonetic First Name" for a phone and
  // "Sexe" for Twitter ("x").
  const words = headers.map(tokens)
  for (const field of FIELD_ORDER) {
    if (matched.has(field)) continue
    const synonyms = SYNONYMS[field].filter((syn) => !WEAK.has(syn))
    const idx = words.findIndex(
      (w, i) =>
        !used.has(i) &&
        !w.some((word) => EXCLUDED.has(word)) &&
        synonyms.some((syn) => hasWordRun(w, syn)),
    )
    if (idx !== -1) assign(field, idx)
  }

  return mapping
}

// Returns a copy of the mapping where the column at `index` holds `field`
// (null = column ignored). A field is read from one column only, so it is taken
// away from the column that had it.
export const setMappingAt = (
  mapping: (CanonicalField | null)[],
  index: number,
  field: CanonicalField | null,
): (CanonicalField | null)[] =>
  mapping.map((current, i) => {
    if (i === index) return field
    return field !== null && current === field ? null : current
  })

const parseCsv = (text: string): { headers: string[]; rows: string[][] } => {
  // Strip a leading UTF-8 BOM that Excel often adds.
  const clean = text.replace(/^﻿/, '')
  // Guess the delimiter from the header line (Excel-FR exports use ";").
  const firstLine = clean.split(/\r?\n/).find((l) => l.trim().length > 0) || ''
  const semis = (firstLine.match(/;/g) || []).length
  const commas = (firstLine.match(/,/g) || []).length
  const delimiter = semis > commas ? ';' : ','

  // Read the whole text in one go: a line break inside quotes belongs to its
  // cell (a note on several lines) and must not start a new contact.
  const records: string[][] = []
  let record: string[] = []
  let cur = ''
  let inQuotes = false
  const endRecord = () => {
    record.push(cur.trim())
    cur = ''
    // Drop empty lines.
    if (record.some((c) => c.length > 0)) records.push(record)
    record = []
  }
  for (let i = 0; i < clean.length; i++) {
    const c = clean[i]
    if (inQuotes) {
      if (c === '"') {
        if (clean[i + 1] === '"') { cur += '"'; i++ } else inQuotes = false
      } else cur += c
    }
    // A quote opens a quoted cell only at the start of the cell: elsewhere it
    // is an ordinary character (24" screen) and must not swallow the next lines.
    else if (c === '"' && cur.trim() === '') inQuotes = true
    else if (c === delimiter) { record.push(cur.trim()); cur = '' }
    else if (c === '\n') endRecord()
    else if (c === '\r' && clean[i + 1] === '\n') { endRecord(); i++ }
    else cur += c
  }
  endRecord()

  if (records.length === 0) return { headers: [], rows: [] }
  return { headers: records[0], rows: records.slice(1) }
}

export const parseContactsFile = async (file: File): Promise<ParsedFile> => {
  const name = (file.name || '').toLowerCase()
  const isCsv = name.endsWith('.csv') || file.type === 'text/csv'
  // CSV is the only supported (and safe) format — reject Excel/binary uploads.
  if (!isCsv) throw new Error(UNSUPPORTED_FORMAT_ERROR)
  const { headers, rows } = parseCsv(await file.text())
  // Drop fully-empty rows.
  const nonEmpty = rows.filter((r) => r.some((c) => c && c.trim().length > 0))
  return { headers, rows: nonEmpty, mapping: detectMapping(headers) }
}

const cell = (
  row: string[],
  mapping: (CanonicalField | null)[],
  field: CanonicalField,
): string => {
  const idx = mapping.indexOf(field)
  // Google Contacts puts several values in one cell ("a@x.be ::: b@x.be"): keep the first.
  return idx === -1 ? '' : (row[idx] || '').split(' ::: ')[0].trim()
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Turns one parsed row into a ghost-profile payload. Returns null when the row
// has no usable identity (no name, email or phone) so it can be skipped.
export const rowToGhostProfile = (
  row: string[],
  mapping: (CanonicalField | null)[],
): CreateGhostProfileData | null => {
  let firstName = cell(row, mapping, 'firstName')
  let lastName = cell(row, mapping, 'lastName')
  const full = cell(row, mapping, 'fullName')
  if (!firstName && !lastName && full) {
    const parts = full.split(/\s+/)
    firstName = parts.shift() || ''
    lastName = parts.join(' ')
  }

  // A value that cannot be an e-mail or a phone number ("* Work", "Mobile") is
  // left out rather than saved on the contact; the contact keeps its other fields.
  const rawEmail = cell(row, mapping, 'email')
  const email = EMAIL_SHAPE.test(rawEmail) ? rawEmail : ''
  const rawPhone = cell(row, mapping, 'phone')
  const phone = (rawPhone.match(/\d/g) || []).length >= 5 ? rawPhone : ''
  const company = cell(row, mapping, 'company')
  const jobTitle = cell(row, mapping, 'jobTitle')
  const website = cell(row, mapping, 'website')
  const linkedin = cell(row, mapping, 'linkedin')
  const instagram = cell(row, mapping, 'instagram')
  const facebook = cell(row, mapping, 'facebook')
  const twitter = cell(row, mapping, 'twitter')
  const notes = cell(row, mapping, 'notes')

  if (!firstName && !lastName && !email && !phone) return null

  // The ghost profile only stores professions by id, so a free-text job title
  // is preserved in the bio alongside any notes column.
  const bioParts = [jobTitle && `Poste : ${jobTitle}`, notes].filter(Boolean)

  const contacts = { email, phone, website, linkedin, instagram, facebook, twitter }
  const hasContact = Object.values(contacts).some((v) => v)

  return {
    scope: Scope.PRIVATE,
    firstName: firstName || undefined,
    lastName: lastName || undefined,
    company: company || undefined,
    bio: bioParts.length ? bioParts.join('\n') : undefined,
    contacts: hasContact
      ? {
          email: email || null,
          phone: phone || null,
          website: website || null,
          linkedin: linkedin || null,
          instagram: instagram || null,
          facebook: facebook || null,
          twitter: twitter || null,
        }
      : undefined,
  }
}

// What tells two contacts apart for the import: the e-mail, the phone (its last
// 9 digits, so "+32 470 00 00 00" and "0470000000" are the same number) and,
// only when there is neither, the full name.
export const identityKeys = (profile: {
  firstName?: string | null
  lastName?: string | null
  contacts?: { email?: string | null; phone?: string | null } | null
}): string[] => {
  const keys: string[] = []
  const email = (profile.contacts?.email || '').trim().toLowerCase()
  if (email) keys.push(`e:${email}`)
  const digits = (profile.contacts?.phone || '').replace(/\D/g, '')
  if (digits) keys.push(`p:${digits.slice(-9)}`)
  if (keys.length === 0) {
    // Every letter is kept, whatever the alphabet: only accents, capitals and
    // extra spaces are ignored, so "Anna Иванова" is not "Anna Петрова".
    const name = `${profile.firstName || ''} ${profile.lastName || ''}`
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()
    if (name) keys.push(`n:${name}`)
  }
  return keys
}
